import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { FaGithub, FaGitlab } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import repoService, { RepositorySearchResult } from '../services/repoService';
import type { OAuthProvider } from '../types/api';
import RepositorySearcher from './RepositorySearcher';
import { oauthStateManager } from '../utils/oauthStateManager';
import { getApiErrorMessage } from '../constants/messages';
import Logger from '../utils/logger';
import { useTranslation } from 'react-i18next';

const RIcon: React.FC<{ icon: IconType; className?: string }> = ({ icon, className }) => {
  const Icon = icon as React.ComponentType<{ className?: string }>;
  return <Icon className={className} />;
};

interface RepositoryConnectorV2Props {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
  projectId?: string;
}

type FlowStep = 'provider' | 'oauth' | 'search' | 'confirm' | 'success' | 'error';

const RepositoryConnectorV2: React.FC<RepositoryConnectorV2Props> = ({
  isOpen,
  onClose,
  onConnected,
  projectId
}) => {
  const { t } = (useTranslation as any)('builds');
  const [step, setStep] = useState<FlowStep>('provider');
  // LE NOM DU FOURNISSEUR TEL QUE L'API LE DECLARE. L'union figee `'github' |
  // 'gitlab'` interdisait `github_app` -- le compilateur l'aurait dit, mais la
  // liste des boutons etait codee en dur, donc personne ne le lui demandait.
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [providers, setProviders] = useState<OAuthProvider[] | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [selectedRepository, setSelectedRepository] = useState<RepositorySearchResult | null>(null);
  const [monitoredBranch, setMonitoredBranch] = useState('main');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [authWindow, setAuthWindow] = useState<Window | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('provider');
      setSelectedProvider(null);
      setState(null);
      setSelectedRepository(null);
      setMonitoredBranch('main');
      setIsLoading(false);
      setErrorMessage('');
    }
  }, [isOpen]);

  // LA CONSOLE NE DEVINE PLUS QUELS FOURNISSEURS EXISTENT.
  // Un echec laisse `providers` a `null` : on retombe alors sur les deux boutons
  // historiques. Ne pas savoir ne doit pas empecher de connecter un depot -- et si
  // le fournisseur est vraiment indisponible, `authorize` le dira avec un message.
  useEffect(() => {
    if (!isOpen) return;
    let vivant = true;
    repoService.listOAuthProviders()
      .then((liste) => { if (vivant) setProviders(liste); })
      .catch((e) => {
        Logger.error('Liste des fournisseurs OAuth indisponible:', e);
        if (vivant) setProviders(null);
      });
    return () => { vivant = false; };
  }, [isOpen]);

  // L'APP EST PREFEREE A L'OAUTH APP quand elle est prete : c'est la migration, et
  // elle ne demande rien a la personne -- le bouton s'appelle toujours « GitHub ».
  // `prete === true` et jamais `!== false` : le champ est optionnel (cf.
  // `OAuthProvider`), et absent veut dire « on ne sait pas ».
  const github = providers
    ? (providers.find((p) => p.auth_kind === 'github_app' && p.prete === true)
       || providers.find((p) => p.name === 'github' && p.prete === true))
    : undefined;
  const gitlab = providers
    ? providers.find((p) => p.name === 'gitlab' && p.prete === true)
    : undefined;
  const listeInconnue = providers === null;
  const parApp = github?.auth_kind === 'github_app';
  const libelleFournisseur =
    providers?.find((p) => p.name === selectedProvider)?.display_name
    || selectedProvider
    || 'GitHub';

  // Close orphaned OAuth popup on unmount or modal close
  useEffect(() => {
    return () => {
      if (authWindow && !authWindow.closed) {
        authWindow.close();
      }
    };
  }, [authWindow]);

  // Close orphaned OAuth popup on unmount or modal close
  useEffect(() => {
    return () => {
      if (authWindow && !authWindow.closed) {
        authWindow.close();
      }
    };
  }, [authWindow]);

  // L'ETAT QUI A ABOUTI, PAS LE DERNIER DEMANDE.
  //
  // La popup renvoie l'etat exact que GitHub vient de valider -- dans son
  // `postMessage` ET dans `oauth_callback_result`. Le connecteur lisait cet objet,
  // verifiait son `type`, puis appelait la verification SANS lui passer son
  // `state` : elle retombait sur l'etat React, c'est-a-dire sur le DERNIER
  // `authorize` emis. Un second clic (ou une simple reouverture du modal, qui
  // remet `state` a null) suffisait donc a chercher sur une session qui n'avait
  // jamais vu de callback -- 409, et la console tourne indefiniment sur
  // « Installation en cours... ». Mesure en dev le 2026-10-24 : deux sessions en
  // Redis, celle de 14:21:54 avec son installation, celle de 14:22:40 sans, et
  // c'est la seconde qui etait interrogee.
  //
  // On ecoute donc les DEUX canaux que la popup emet. `postMessage` etait emis et
  // personne ne l'ecoutait ; `storage` ne se declenche pas si la popup et
  // l'ouvrant sont le meme contexte. Deux canaux, un seul traitement, dedoublonne
  // par `dernierTraite` -- sinon la personne voit deux fois le meme toast.
  const verifierRef = useRef<(etat?: string) => void>(() => {});
  const dernierTraite = useRef<string | null>(null);
  // Une REFERENCE et non `authWindow` : l'ecoute est posee une fois pour
  // l'ouverture du modal, elle capturerait sinon la popup du premier clic.
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const traiter = (donnees: unknown) => {
      const d = donnees as { type?: string; state?: string } | null;
      if (!d || d.type !== 'oauth_success' || !d.state) return;
      if (dernierTraite.current === d.state) return;
      dernierTraite.current = d.state;
      try { localStorage.removeItem('oauth_callback_result'); } catch { /* ignore */ }
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
      verifierRef.current(d.state);
    };
    const surStorage = (e: StorageEvent) => {
      if (e.key !== 'oauth_callback_result' || !e.newValue) return;
      try { traiter(JSON.parse(e.newValue)); } catch { /* charge illisible */ }
    };
    const surMessage = (e: MessageEvent) => {
      // Meme origine seulement : ce canal accepte n'importe quel emetteur.
      if (e.origin !== window.location.origin) return;
      traiter(e.data);
    };
    window.addEventListener('storage', surStorage);
    window.addEventListener('message', surMessage);
    return () => {
      window.removeEventListener('storage', surStorage);
      window.removeEventListener('message', surMessage);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Étape 1: Sélectionner le provider
  const handleProviderSelect = async (provider: string) => {
    try {
      setIsLoading(true);
      setSelectedProvider(provider);

      // Initier OAuth v2
      const response = await repoService.initiateOAuthV2(provider);

      // Sauvegarder le state avec expiration
      oauthStateManager.setState(response.state, provider);
      setState(response.state);

      // Ouvrir la fenêtre OAuth
      const oauthPopup = window.open(
        response.auth_url,
        'oauth_v2',
        'width=700,height=800,left=200,top=100'
      );

      if (!oauthPopup) {
        toast.error('Impossible d\'ouvrir la fenêtre OAuth. Vérifiez les pop-ups.');
        setIsLoading(false);
        return;
      }

      setAuthWindow(oauthPopup);
      popupRef.current = oauthPopup;

      // L'ecoute du retour vit au niveau du composant (cf. plus haut) : posee ici,
      // elle s'ajoutait a CHAQUE clic, ne se retirait qu'en se declenchant, et
      // appelait la verification sans l'etat que la popup venait de rapporter.
      setStep('oauth');
    } catch (error: unknown) {
      Logger.error('Erreur lors de l\'initiation OAuth:', error);
      toast.error(getApiErrorMessage(error) || 'Erreur lors de l\'autorisation');
      setStep('error');
      setErrorMessage(getApiErrorMessage(error) || 'Erreur lors de l\'autorisation');
    } finally {
      setIsLoading(false);
    }
  };

  // L'etat du dernier retour de popup encore en attente, s'il y en a un.
  //
  // REPLI DU BOUTON « C'est fait ». Le modal remet `state` a null a chaque
  // ouverture : si la personne le rouvre entre le callback et son clic, l'etat
  // qui a abouti n'est plus en memoire. Il est, lui, dans `oauth_callback_result`.
  const etatRapporteParLaPopup = (): string | null => {
    try {
      const brut = localStorage.getItem('oauth_callback_result');
      if (!brut) return null;
      const d = JSON.parse(brut) as { type?: string; state?: string };
      return d.type === 'oauth_success' && d.state ? d.state : null;
    } catch {
      return null;
    }
  };

  // Vérifier si la session OAuth est valide (appelé manuellement ou par polling)
  //
  // `etatAbouti` est PRIORITAIRE sur l'etat React : c'est celui que GitHub vient
  // de valider. Les interroger dans l'autre sens rejouerait le defaut.
  const checkOAuthSession = async (etatAbouti?: string) => {
    const cible = etatAbouti || state || etatRapporteParLaPopup();
    if (!cible) {
      // Ne pas rester muet : « rien ne se passe » est le symptome qu'on corrige.
      toast.error("Aucune autorisation en cours. Relancez la connexion du depot.");
      return;
    }
    try {
      setIsLoading(true);
      // Tenter de lister les repos - si ça marche, l'OAuth est ok
      await repoService.searchRepositories(cible, '', 1, 0);
      // L'etat retenu doit etre celui qu'on vient de valider : les etapes
      // suivantes (recherche, puis `connect`) s'en servent, et connecter sur un
      // autre etat poserait un depot sans installation.
      if (cible !== state) setState(cible);
      toast.success(`Connecte avec succes a ${selectedProvider || libelleFournisseur}!`);
      setStep('search');
    } catch {
      toast.error('Autorisation non terminée. Veuillez vous connecter dans la fenêtre popup.');
    } finally {
      setIsLoading(false);
    }
  };

  // Le canal d'ecoute, pose avant le rendu, appelle la verification par cette
  // reference -- elle n'existe qu'ici, apres le retour anticipe.
  verifierRef.current = (etat?: string) => { void checkOAuthSession(etat); };

  // Étape 2: Attendre la sélection du repo
  const handleRepositorySelected = (repo: RepositorySearchResult) => {
    setSelectedRepository(repo);
    setStep('confirm');
  };

  // Étape 3: Confirmer et connecter
  const handleConfirmConnection = async () => {
    if (!state || !selectedRepository) {
      toast.error('Données manquantes');
      return;
    }

    try {
      setIsLoading(true);

      // Connecter le repository
      const response = await repoService.connectRepository(
        state,
        selectedRepository.owner,
        selectedRepository.name,
        projectId,
        monitoredBranch
      );

      // Vérifier l'état du webhook
      if (response.webhook_enabled) {
        toast.success('Repository connecté et webhook installé!');
      } else {
        toast.success('Repository connecté (webhook en attente de configuration)');
      }

      setStep('success');

      // Nettoyer le state OAuth
      if (state) {
        oauthStateManager.removeState(state);
      }

      // Fermer après 2 secondes
      setTimeout(() => {
        onConnected();
        onClose();
      }, 2000);
    } catch (error: unknown) {
      Logger.error('Erreur lors de la connexion:', error);
      toast.error(getApiErrorMessage(error) || 'Erreur lors de la connexion');
      setErrorMessage(getApiErrorMessage(error) || 'Erreur lors de la connexion');
      setStep('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface-2 rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-accent-600 to-accent-700 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {step === 'provider' && 'Connecter un Repository'}
            {step === 'oauth' && 'Autorisation OAuth'}
            {step === 'search' && 'Sélectionner un Repository'}
            {step === 'confirm' && 'Confirmer la Connexion'}
            {step === 'success' && 'Succès!'}
            {step === 'error' && 'Erreur'}
          </h2>
          {step !== 'oauth' && (
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded p-1"
            >
              ✕
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Étape 1: Sélectionner provider */}
          {step === 'provider' && (
            <div className="space-y-4">
              <p className="text-text-tertiary text-sm">
                Choisissez votre fournisseur Git pour commencer
              </p>
              {(listeInconnue || github) && (
                <div className="space-y-2">
                  <button
                    onClick={() => handleProviderSelect(github?.name || 'github')}
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <RIcon icon={FaGithub} className="w-5 h-5" />
                    GitHub
                  </button>
                  {parApp && (
                    <p className="text-text-tertiary text-xs">
                      {t('connectorV2.githubAppHint')}
                    </p>
                  )}
                </div>
              )}
              {(listeInconnue || gitlab) && (
                <button
                  onClick={() => handleProviderSelect(gitlab?.name || 'gitlab')}
                  disabled={isLoading}
                  className="w-full px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <RIcon icon={FaGitlab} className="w-5 h-5" />
                  GitLab
                </button>
              )}
            </div>
          )}

          {/* Étape 2: OAuth en cours */}
          {step === 'oauth' && (
            <div className="space-y-4 text-center">
              <div className="flex justify-center mb-4">
                <div className="animate-spin h-12 w-12 border-4 border-accent-500 border-t-transparent rounded-full" />
              </div>
              <h3 className="font-semibold text-text-primary">
                {parApp ? t('connectorV2.installTitle') : 'Autorisation en cours...'}
              </h3>
              <p className="text-text-tertiary text-sm">
                {parApp ? t('connectorV2.installDesc') : (
                  <>Connectez-vous dans la fenêtre qui s'est ouverte,
                  puis cliquez sur le bouton ci-dessous.</>
                )}
              </p>
              <button
                onClick={() => checkOAuthSession()}
                disabled={isLoading}
                className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Vérification...' : 'C\'est fait, choisir mon repo'}
              </button>
              <button
                onClick={() => {
                  setStep('provider');
                  setState(null);
                  setSelectedProvider(null);
                }}
                className="w-full px-4 py-2 text-text-tertiary hover:text-text-secondary text-sm transition-colors"
              >
                Annuler
              </button>
            </div>
          )}

          {/* Étape 3: Recherche et sélection */}
          {step === 'search' && state && (
            <div className="space-y-4">
              <p className="text-text-tertiary text-sm">
                Recherchez et sélectionnez le repository à connecter
              </p>
              <RepositorySearcher
                state={state}
                provider={libelleFournisseur}
                onRepositorySelected={handleRepositorySelected}
                isLoading={isLoading}
              />
            </div>
          )}

          {/* Étape 4: Confirmation */}
          {step === 'confirm' && selectedRepository && (
            <div className="space-y-4">
              <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-text-primary mb-2">
                  {selectedRepository.full_name}
                </h3>
                {selectedRepository.description && (
                  <p className="text-sm text-text-tertiary mb-3">
                    {selectedRepository.description}
                  </p>
                )}
                <div className="space-y-2 text-sm text-text-tertiary">
                  <p>
                    <span className="font-medium">Branche par défaut:</span> {selectedRepository.default_branch}
                  </p>
                  <p>
                    <span className="font-medium">Type:</span> {selectedRepository.private ? 'Privé' : 'Public'}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-secondary">
                  Branche à surveiller pour les auto-builds
                </label>
                <input
                  type="text"
                  value={monitoredBranch}
                  onChange={(e) => setMonitoredBranch(e.target.value)}
                  placeholder="main"
                  className="w-full px-3 py-2 border border-border-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm text-text-secondary">
                <p>
                  <SparklesIcon className="w-4 h-4 inline mr-1" />
                  {/* POUR UNE APP CETTE PHRASE SERAIT FAUSSE : l'app a UN webhook
                      declare sur elle-meme, rien n'est pose par depot. Annoncer une
                      installation qui n'a pas lieu ferait chercher une panne
                      inexistante le jour ou un build ne part pas. */}
                  {parApp
                    ? t('connectorV2.appWebhookInfo')
                    : 'Un webhook sera installé automatiquement. Pensez ensuite à configurer le build automatique depuis les paramètres du repository.'}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('search')}
                  className="flex-1 px-4 py-2 border border-border-1 rounded-lg hover:bg-surface-3 font-medium transition-colors"
                >
                  Retour
                </button>
                <button
                  onClick={handleConfirmConnection}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Connexion...' : 'Connecter'}
                </button>
              </div>
            </div>
          )}

          {/* Succès */}
          {step === 'success' && (
            <div className="space-y-4 text-center">
              <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto" />
              <h3 className="font-semibold text-text-primary text-lg">
                Connexion réussie!
              </h3>
              <p className="text-text-tertiary text-sm">
                {selectedRepository?.full_name} a été connecté avec succès.
                Configurez les builds automatique sur la branche de votre choix.
              </p>
              <p className="text-xs text-text-tertiary">
                Fermeture automatique dans 2 secondes...
              </p>
            </div>
          )}

          {/* Erreur */}
          {step === 'error' && (
            <div className="space-y-4">
              <XCircleIcon className="w-12 h-12 text-red-500 mx-auto" />
              <h3 className="font-semibold text-text-primary text-center">
                Une erreur s'est produite
              </h3>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">
                {errorMessage || 'Une erreur inconnue s\'est produite'}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    // Clear OAuth state if exists
                    if (state) {
                      oauthStateManager.removeState(state);
                    }
                    setStep('provider');
                    setErrorMessage('');
                    setState(null);
                    setSelectedRepository(null);
                    setSelectedProvider(null);
                  }}
                  className="flex-1 px-4 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium transition-colors"
                >
                  Recommencer
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-border-1 rounded-lg hover:bg-surface-3 font-medium transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepositoryConnectorV2;
